// src/components/Dashboard/Dashboard.js

import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Switch, TextField, Chip,
  Checkbox, FormControlLabel, Button, Alert, Snackbar,
  FormControl, Select, MenuItem, CircularProgress, Autocomplete, Tooltip,
  Paper, Stack, Stepper, Step, StepLabel, StepContent,
  Drawer, Grid, Icon, IconButton, Divider, Card, CardContent, Avatar, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './Dashboard.css';
import prospectService from '../../services/prospectService';
import GetAppIcon from '@mui/icons-material/GetApp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FlagIcon from '@mui/icons-material/Flag';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';

const Dashboard = () => {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [availableFilters, setAvailableFilters] = useState([]);
  const [totalProspects, setTotalProspects] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);

  // Array of filters to hide
  const filtersToHide = ['estadoAfiliacion'];

  // Add utility function for date formatting
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      // Handle ISO date format (2023-01-01T00:00:00)
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date format: ${dateString}`);
        return '-';
      }
      
      // Format as DD/MM/YYYY using UTC methods to avoid timezone issues
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error(`Error formatting date: ${dateString}`, error);
      return '-';
    }
  };

  // Function to check if a prospect has been processed by Apollo
  const isProcessedByApollo = (row) => {
    // Check if datosApollo is not empty or if there's an apolloProcessedDate property
    return (row.datosApollo && row.datosApollo.trim && row.datosApollo.trim() !== '') || 
           (row.apolloProcessedDate !== null && row.apolloProcessedDate !== undefined);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Obtener la definición de filtros desde el backend
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setLoadingFilters(true);
        console.log('Iniciando carga de filtros...');
        const filtersResponse = await prospectService.getFilters();
        console.log('Respuesta de filtros recibida:', filtersResponse);
        
        if (!filtersResponse || !Array.isArray(filtersResponse)) {
          console.error('Error: La respuesta de filtros no es un array', filtersResponse);
          setSnackbar({
            open: true,
            message: 'Error al cargar los filtros: formato de respuesta inválido',
            severity: 'error'
          });
          setAvailableFilters([]);
          return;
        }
        
        setAvailableFilters(filtersResponse);

        // Inicializar el estado de los filtros
        const initialFiltersState = filtersResponse.reduce((acc, filter) => {
          acc[filter.name] = null; // Inicializar todos los filtros a null
          return acc;
        }, {});
        setFilters(initialFiltersState);
        console.log('Filtros cargados correctamente:', filtersResponse.length);
      } catch (error) {
        console.error('Error detallado al cargar los filtros:', error);
        // Mostrar información más detallada del error
        let errorMessage = 'Error al cargar los filtros';
        if (error.response) {
          console.error('Error response:', error.response);
          errorMessage += `: ${error.response.status} ${error.response.statusText}`;
        } else if (error.request) {
          console.error('Error request:', error.request);
          errorMessage += ': No se recibió respuesta del servidor';
        } else {
          console.error('Error message:', error.message);
          errorMessage += `: ${error.message}`;
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
        setAvailableFilters([]);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchFilters();
  }, []);

  // Función para limpiar filtros antes de enviarlos al backend
  const cleanFilters = (filters) => {
    // Crear una copia de los filtros para no modificar el estado original
    const cleanedFilters = {};
    
    // Procesar cada filtro
    Object.entries(filters).forEach(([key, value]) => {
      // Ignorar filtros nulos, undefined o vacíos
      if (value === null || value === undefined || value === '') {
        return;
      }
      
      // Procesar según el tipo de filtro
      if (key === 'nombre' || key === 'apellido') {
        // Para nombre y apellido, asegurar que sea case-insensitive
        cleanedFilters[key] = {
          value: value.toString(),
          ignoreCase: true
        };
      } else if (key === 'cedula') {
        // Para cédula, aplicar trim para eliminar espacios
        cleanedFilters[key] = value.toString().trim();
      } else if (key === 'fechaDeNacimiento' && typeof value === 'object') {
        // Para fechas, verificar que tenga start o end
        if ((value.start && value.start.trim() !== '') || (value.end && value.end.trim() !== '')) {
          // Asegurar que el objeto tiene la estructura correcta esperada por el backend
          cleanedFilters[key] = {
            start: value.start || null,
            end: value.end || null
          };
          console.log('Incluyendo filtro de fecha:', cleanedFilters[key]);
        }
      } else {
        // Para otros tipos, pasar el valor directamente
        cleanedFilters[key] = value;
      }
    });
    
    console.log('Filtros procesados:', cleanedFilters);
    return cleanedFilters;
  };

  // Obtener los prospectos al cargar el componente o cuando cambian los filtros
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const cleanedFilters = cleanFilters(filters);
        const filterModel = { Filters: cleanedFilters };
        console.log('Enviando solicitud de prospectos con filtros:', JSON.stringify(filterModel, null, 2));
        
        // Log each filter for debugging
        Object.entries(cleanedFilters).forEach(([key, value]) => {
          console.log(`Filtro ${key}:`, typeof value === 'object' ? JSON.stringify(value) : value);
        });
        
        const response = await prospectService.getProspects(filterModel, page + 1, rowsPerPage);
        console.log('Respuesta recibida de prospectos:', response);
        
        if (!response) {
          console.error('No se recibió respuesta del servicio de prospectos');
          setRows([]);
          setTotalProspects(0);
          return;
        }

        // Verificar si la respuesta tiene la estructura esperada
        if (!response.prospects) {
          console.error('La respuesta no tiene la propiedad "prospects":', response);
          // Intentar determinar si la respuesta es directamente el array de prospectos
          if (Array.isArray(response)) {
            console.log('La respuesta es un array, asumiendo que son los prospectos');
            setRows(response);
            setTotalProspects(response.length);
            return;
          }
          setRows([]);
          setTotalProspects(0);
          return;
        }

        // Process all fields from the response
        console.log('Datos procesados para mostrar:', response.prospects);
        setRows(response.prospects);
        setTotalProspects(response.total);
      } catch (error) {
        console.error('Error detallado al cargar los datos:', error);
        setSnackbar({
          open: true,
          message: `Error al cargar los datos: ${error.message || 'Error desconocido'}`,
          severity: 'error'
        });
        setRows([]);
        setTotalProspects(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters, page, rowsPerPage]);

  const handleFilterChange = (key, value) => {
    console.log(`Cambiando filtro ${key}:`, value);
    
    // Special handling for daterange filters
    if (key === 'fechaDeNacimiento') {
      // Ensure the value has the correct structure
      const dateFilter = {
        start: value?.start || null,
        end: value?.end || null
      };
      
      // Only set the filter if at least one date is provided
      if (dateFilter.start || dateFilter.end) {
        setFilters(prev => ({ ...prev, [key]: dateFilter }));
      } else {
        // If both dates are null/empty, remove the filter
        setFilters(prev => {
          const newFilters = { ...prev };
          delete newFilters[key];
          return newFilters;
        });
      }
    } else {
      // Handle other filter types
      setFilters(prev => ({ ...prev, [key]: value }));
    }
    
    setPage(0); // Reset to first page when filter changes
  };

  const handleFilterDelete = (key) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      // Completely remove the filter instead of setting to null
      delete newFilters[key];
      return newFilters;
    });
    setPage(0); // Reset to first page when filter is deleted
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const getFormattedRows = (prospects) => {
    return prospects.map(prospect => ({
      Cedula: prospect.cedula || '',
      Nombre: prospect.nombre || '',
      Apellido: prospect.apellido || '',
      Email: prospect.email || '',
      Telefono: prospect.telefono || '',
      Universidad: prospect.universidad || '',
      Empresa: prospect.empresa || '',
      'Cargo Actual': prospect.jobTitle || '',
      'Fecha de Nacimiento': formatDate(prospect.fechaDeNacimiento),
      Localidad: prospect.localidad?.value || prospect.localidad || '',
      Departamento: prospect.departamento?.value || prospect.departamento || '',
      'País': prospect.paisDeNacimiento || '',
      'Estado de Afiliación': prospect.mensajeBPS || '',
      'LinkedIn URL': prospect.linkedInUrl || '',
      'Estado Apollo': prospect.apolloProcessedDate ? (prospect.datosApollo || 'Procesado') : 'Pendiente',
      'Batch': prospect.batchName || '',
      'Teléfono Empresa': prospect.apolloData?.organization?.primaryPhoneSanitized || '',
      'Ciudad': prospect.apolloData?.city || '',
      'Estado': prospect.apolloData?.state || '',
      'País Apollo': prospect.apolloData?.country || '',
    }));
  };

  // Add a function to format employment history for tooltip display
  const formatEmploymentHistory = (history) => {
    if (!history || !Array.isArray(history) || history.length === 0) {
      return 'No hay historial laboral disponible';
    }

    // Sort by date (most recent first)
    const sortedHistory = [...history].sort((a, b) => {
      // Current jobs first
      if (a.current && !b.current) return -1;
      if (!a.current && b.current) return 1;
      
      // Then by start date (most recent first)
      const aStart = a.startDate ? new Date(a.startDate) : new Date(0);
      const bStart = b.startDate ? new Date(b.startDate) : new Date(0);
      return bStart - aStart;
    });

    // Format each entry with better spacing
    return sortedHistory.map((job, index) => {
      const company = job.organizationName || 'Empresa no especificada';
      const title = job.title || 'Cargo no especificado';
      const startDate = job.startDate ? new Date(job.startDate).toLocaleDateString('es-UY') : 'Fecha no especificada';
      const endDate = job.current ? 'Presente' : (job.endDate ? new Date(job.endDate).toLocaleDateString('es-UY') : 'Fecha no especificada');
      
      return (
        `${index + 1}. ${title} en ${company}\n` +
        `   ${startDate} - ${endDate}${job.current ? ' (Actual)' : ''}`
      );
    }).join('\n\n');
  };

  // Update the exportToExcel function to include all Apollo data
  const exportToExcel = async () => {
    try {
      const cleanedFilters = cleanFilters(filters);
      const filterModel = { Filters: cleanedFilters };
      const response = await prospectService.getAllProspects(filterModel);
      
      if (!response || !response.prospects || response.prospects.length === 0) {
        setSnackbar({
          open: true,
          message: 'No hay datos para exportar',
          severity: 'warning'
        });
        return;
      }

      const allRows = getFormattedRows(response.prospects);

      // Add additional Apollo data fields
      const enhancedRows = response.prospects.map(prospect => {
        const baseRow = {
          ID: prospect.id,
          Cedula: prospect.cedula || '',
          Nombre: prospect.nombre || '',
          Apellido: prospect.apellido || '',
          Email: prospect.email || '',
          Telefono: prospect.telefono || '',
          'Teléfono Empresa': prospect.apolloData?.organization?.primaryPhoneSanitized || '',
          Universidad: prospect.universidad || '',
          'Cargo Actual': prospect.jobTitle || '',
          Empresa: prospect.empresa || '',
          'Fecha de Nacimiento': formatDate(prospect.fechaDeNacimiento),
          Localidad: prospect.localidad?.value || prospect.localidad || '',
          Departamento: prospect.departamento?.value || prospect.departamento || '',
          'País': prospect.paisDeNacimiento || '',
          'Estado de Afiliación': prospect.mensajeBPS || '',
          'LinkedIn URL': prospect.linkedInUrl || '',
          'Ciudad (Apollo)': prospect.apolloData?.city || '',
          'Estado/Departamento (Apollo)': prospect.apolloData?.state || '',
          'País (Apollo)': prospect.apolloData?.country || '',
          'ID Apollo': prospect.apolloId || '',
          'Fecha Procesado Apollo': formatDate(prospect.apolloProcessedDate),
          'Batch': prospect.batchName || ''
        };

        // Add employment history if available
        if (prospect.apolloData?.employmentHistory && prospect.apolloData.employmentHistory.length > 0) {
          const history = prospect.apolloData.employmentHistory;
          
          // Find current job
          const currentJob = history.find(job => job.current);
          if (currentJob) {
            baseRow['Empresa Actual'] = currentJob.organizationName || '';
            baseRow['Cargo Actual (Apollo)'] = currentJob.title || '';
            baseRow['Fecha Inicio Actual'] = currentJob.startDate ? new Date(currentJob.startDate).toLocaleDateString('es-UY') : '';
          }

          // Add previous jobs (up to 3)
          const previousJobs = history.filter(job => !job.current)
            .sort((a, b) => {
              const aDate = a.endDate ? new Date(a.endDate) : new Date(0);
              const bDate = b.endDate ? new Date(b.endDate) : new Date(0);
              return bDate - aDate; // Most recent first
            })
            .slice(0, 3);

          previousJobs.forEach((job, index) => {
            baseRow[`Empresa Anterior ${index+1}`] = job.organizationName || '';
            baseRow[`Cargo Anterior ${index+1}`] = job.title || '';
            baseRow[`Período ${index+1}`] = `${job.startDate ? new Date(job.startDate).toLocaleDateString('es-UY') : ''} - ${job.endDate ? new Date(job.endDate).toLocaleDateString('es-UY') : ''}`;
          });
        }

        return baseRow;
      });

      const worksheet = XLSX.utils.json_to_sheet(enhancedRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Prospectos');
      
      // Adjust column widths
      const maxWidth = 50;
      const cols = Object.keys(enhancedRows[0]).map(key => ({ wch: Math.min(key.length + 2, maxWidth) }));
      worksheet['!cols'] = cols;
      
      XLSX.writeFile(workbook, 'prospectos_completo.xlsx');
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      setSnackbar({
        open: true,
        message: 'Error al exportar a Excel',
        severity: 'error'
      });
    }
  };

  const exportToPDF = async () => {
    try {
      const cleanedFilters = cleanFilters(filters);
      const filterModel = { Filters: cleanedFilters };
      const response = await prospectService.getAllProspects(filterModel);

      if (!response || !response.prospects || response.prospects.length === 0) {
        setSnackbar({
          open: true,
          message: 'No hay datos para exportar',
          severity: 'warning'
        });
        return;
      }

      const doc = new jsPDF('l', 'mm', 'a4');
      const allRows = getFormattedRows(response.prospects);
      
      const headers = [
        ['Cédula', 'Nombre', 'Apellido', 'Email', 'Teléfono', 'Teléfono Empresa', 'Universidad', 
         'Cargo', 'Empresa', 'Fecha de Nacimiento', 'Localidad', 'Departamento', 
         'País', 'Estado de Afiliación', 'LinkedIn', 'Estado Apollo', 'Batch']
      ];

      const data = allRows.map(row => [
        row.Cedula,
        row.Nombre,
        row.Apellido,
        row.Email,
        row.Telefono,
        row['Teléfono Empresa'],
        row.Universidad,
        row['Cargo Actual'],
        row.Empresa,
        row['Fecha de Nacimiento'],
        row.Localidad,
        row.Departamento,
        row.País,
        row['Estado de Afiliación'],
        row['LinkedIn URL'],
        row['Estado Apollo'],
        row['Batch']
      ]);

      doc.autoTable({
        head: headers,
        body: data,
        theme: 'grid',
        styles: {
          fontSize: 7,
          cellPadding: 1
        },
        columnStyles: {
          0: { cellWidth: 15 }, // Cedula
          1: { cellWidth: 15 }, // Nombre
          2: { cellWidth: 15 }, // Apellido
          3: { cellWidth: 20 }, // Email
          4: { cellWidth: 12 }, // Telefono
          5: { cellWidth: 15 }, // Teléfono Empresa
          6: { cellWidth: 15 }, // Universidad
          7: { cellWidth: 15 }, // Cargo
          8: { cellWidth: 15 }, // Empresa
          9: { cellWidth: 12 }, // Fecha de Nacimiento
          10: { cellWidth: 12 }, // Localidad
          11: { cellWidth: 12 }, // Departamento
          12: { cellWidth: 12 }, // País
          13: { cellWidth: 15 }, // Estado de Afiliacion
          14: { cellWidth: 15 }, // LinkedIn
          15: { cellWidth: 15 }, // Estado Apollo
          16: { cellWidth: 15 }  // Batch
        },
        margin: { top: 10 }
      });
      doc.save('prospectos.pdf');
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error al exportar a PDF',
        severity: 'error'
      });
    }
  };

  // Function to handle row click and open drawer
  const handleRowClick = (prospect) => {
    setSelectedProspect(prospect);
    setDrawerOpen(true);
  };

  // Function to close drawer
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const renderFilterControl = (filter) => {
    const { name, type, label, options } = filter;

    if (filtersToHide.includes(name)) {
      return null;
    }

    switch (type) {
      case 'batch':
        return (
          <Autocomplete
            className="filter-input"
            options={options || []}
            value={filters[name] || null}
            onChange={(event, newValue) => handleFilterChange(name, newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={`Buscar por ${label.toLowerCase()}`}
              />
            )}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1B285E',
                  }
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1B285E',
                    borderWidth: '2px'
                  }
                }
              }
            }}
          />
        );
      case 'boolean':
        return (
          <FormControlLabel
            className="filter-switch"
            control={
              <Switch
                checked={filters[name] || false}
                onChange={(e) => handleFilterChange(name, e.target.checked)}
              />
            }
            label={filters[name] ? 'Sí' : 'No'}
          />
        );
      case 'select':
        return (
          <FormControl variant="outlined" size="small" className="filter-select">
            <Select
              multiple
              value={filters[name] || []}
              onChange={(e) => handleFilterChange(name, e.target.value)}
              displayEmpty
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    position: 'fixed',
                    top: '200px',
                    left: '280px',
                    minWidth: '200px'
                  }
                },
                disablePortal: true,
                keepMounted: true,
                slotProps: {
                  paper: {
                    elevation: 4,
                    sx: {
                      overflow: 'visible',
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        left: 14,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    }
                  }
                }
              }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>Todos</em>;
                }
                return selected.join(', ');
              }}
            >
              {options.map((value, index) => (
                <MenuItem key={index} value={value}>
                  <Checkbox checked={(filters[name] || []).indexOf(value) > -1} />
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'range':
        return (
          <Box>
            <TextField
              className="filter-input"
              type="number"
              placeholder="Mínimo"
              value={filters[name]?.[0] || ''}
              variant="outlined"
              size="small"
              onChange={(e) =>
                handleFilterChange(name, [
                  e.target.value ? parseInt(e.target.value, 10) : null,
                  filters[name]?.[1]
                ])
              }
            />
            <TextField
              className="filter-input"
              type="number"
              placeholder="Máximo"
              value={filters[name]?.[1] || ''}
              variant="outlined"
              size="small"
              onChange={(e) =>
                handleFilterChange(name, [
                  filters[name]?.[0],
                  e.target.value ? parseInt(e.target.value, 10) : null
                ])
              }
            />
          </Box>
        );
      case 'daterange':
        return (
          <Box 
            display="flex" 
            flexDirection="column" 
            gap={2} 
            sx={{ 
              '& .MuiTextField-root': { 
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1B285E',
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1B285E',
                      borderWidth: '2px'
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#1B285E',
                  '&.Mui-focused': {
                    color: '#1B285E'
                  }
                }
              },
              mt: 2,
              mb: 1
            }}
          >
            <TextField
              type="date"
              label="Desde"
              value={filters[name]?.start || ''}
              variant="outlined"
              size="small"
              InputLabelProps={{ 
                shrink: true,
                sx: {
                  backgroundColor: '#fff',
                  padding: '4px 8px',
                  marginTop: '-8px',
                  borderRadius: '4px'
                }
              }}
              onChange={(e) => {
                const value = e.target.value || null;
                console.log(`Cambiando fecha inicio para ${name}:`, value);
                handleFilterChange(name, {
                  ...filters[name],
                  start: value
                });
              }}
            />
            <TextField
              type="date"
              label="Hasta"
              value={filters[name]?.end || ''}
              variant="outlined"
              size="small"
              InputLabelProps={{ 
                shrink: true,
                sx: {
                  backgroundColor: '#fff',
                  padding: '4px 8px',
                  marginTop: '-8px',
                  borderRadius: '4px'
                }
              }}
              onChange={(e) => {
                const value = e.target.value || null;
                console.log(`Cambiando fecha fin para ${name}:`, value);
                handleFilterChange(name, {
                  ...filters[name],
                  end: value
                });
              }}
            />
          </Box>
        );
      case 'date':
        return (
          <TextField
            className="filter-input"
            type="date"
            value={filters[name] || ''}
            variant="outlined"
            size="small"
            onChange={(e) => handleFilterChange(name, e.target.value)}
          />
        );
      default:
        return (
          <TextField
            className="filter-input"
            value={filters[name] || ''}
            placeholder={`Buscar por ${label.toLowerCase()}`}
            variant="outlined"
            size="small"
            onChange={(e) => handleFilterChange(name, e.target.value)}
          />
        );
    }
  };

  // Add a utility function to get current employment safely
  const getCurrentEmployment = (employmentHistory) => {
    if (!employmentHistory || !Array.isArray(employmentHistory) || employmentHistory.length === 0) {
      return null;
    }
    
    // First look for explicit current=true
    const current = employmentHistory.find(job => job.current === true);
    if (current) {
      return current;
    }
    
    // If none marked current, find the most recent by start date
    return employmentHistory.sort((a, b) => {
      const aStart = a.startDate ? new Date(a.startDate) : new Date(0);
      const bStart = b.startDate ? new Date(b.startDate) : new Date(0);
      return bStart - aStart; // Most recent first
    })[0];
  };

  // Render drawer content
  const renderDrawer = () => {
    return (
      <div className="drawer-content">
        <IconButton
          onClick={handleCloseDrawer}
          style={{ position: 'absolute', right: '10px', top: '10px' }}
        >
          <Icon>close</Icon>
        </IconButton>

        <Box className="drawer-header" sx={{ mb: 3, p: 2, pt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={selectedProspect?.photoUrl}
              alt={`${selectedProspect?.nombre} ${selectedProspect?.apellido}`}
              sx={{ 
                width: 80, 
                height: 80, 
                mr: 2, 
                bgcolor: '#1B285E',
                fontSize: '1.8rem',
                border: '2px solid #fff',
                boxShadow: '0 3px 5px rgba(0,0,0,0.2)'
              }}
            >
              {selectedProspect?.nombre?.charAt(0) || ''}{selectedProspect?.apellido?.charAt(0) || ''}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold" color="primary" sx={{ fontSize: '1.5rem', textShadow: '0 1px 1px rgba(0,0,0,0.05)' }}>
                {selectedProspect?.nombre} {selectedProspect?.apellido}
              </Typography>
              
              {(selectedProspect?.empleos && selectedProspect.empleos.find(job => job.esActual)?.titulo) && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 0.5 }}>
                  <Typography variant="body1" color="primary" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    {selectedProspect.empleos.find(job => job.esActual)?.titulo}
                    {selectedProspect.empleos.find(job => job.esActual)?.organizacionNombre && (
                      <span style={{ fontWeight: 400 }}> en {selectedProspect.empleos.find(job => job.esActual)?.organizacionNombre}</span>
                    )}
                  </Typography>
                  <Chip 
                    label="Actual" 
                    size="small" 
                    className="current-job-chip"
                    sx={{ ml: 1, height: 18, fontWeight: 'bold', fontSize: '0.65rem' }}
                  />
                </Box>
              )}
              
              <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500, fontSize: '0.95rem', mt: 0.5 }}>
                CI: {selectedProspect?.cedula}
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                {formatDate(selectedProspect?.fechaDeNacimiento)}
              </Typography>
            </Box>
          </Box>
          
          {selectedProspect?.apolloData?.headline && (
            <Typography 
              variant="body1" 
              sx={{ 
                fontStyle: 'italic', 
                mt: 2, 
                ml: 1,
                pl: 1,
                borderLeft: '3px solid #1976d2',
                color: 'rgba(0, 0, 0, 0.75)',
                fontWeight: 500
              }}
            >
              {selectedProspect.apolloData.headline}
            </Typography>
          )}
        </Box>

        <Box sx={{ px: 2 }}>
          {/* Contact Information Section */}
          {(selectedProspect?.email || selectedProspect?.telefono || selectedProspect?.empresa || selectedProspect?.jobTitle || selectedProspect?.linkedInUrl) && (
            <div className="apollo-data-section">
              <h3>Información de Contacto y Laboral</h3>
              <List>
                {selectedProspect.email && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Tooltip title="Enviar email" placement="top">
                          <a href={`mailto:${selectedProspect.email}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Typography variant="body1" sx={{ 
                              '&:hover': { textDecoration: 'underline', color: '#1976d2' },
                              fontSize: '0.95rem',
                              fontWeight: 500,
                              color: 'rgba(0, 0, 0, 0.87)'
                            }}>
                              {selectedProspect.email}
                            </Typography>
                          </a>
                        </Tooltip>
                      }
                    />
                  </ListItem>
                )}
                {selectedProspect.telefono && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PhoneIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Tooltip 
                          title={
                            <Box>
                              {selectedProspect.telefono && (
                                <Box mb={1}>
                                  <Typography variant="subtitle2" color="primary">Teléfono Personal</Typography>
                                  <Typography>{selectedProspect.telefono}</Typography>
                                </Box>
                              )}
                              {selectedProspect.apolloData?.organization?.primaryPhoneSanitized && (
                                <Box>
                                  <Typography variant="subtitle2" color="primary">
                                    Teléfono de {selectedProspect.apolloData.organization.name || 'la empresa'}
                                  </Typography>
                                  <Typography>{selectedProspect.apolloData.organization.primaryPhoneSanitized}</Typography>
                                </Box>
                              )}
                            </Box>
                          }
                          placement="left"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'white',
                                color: 'rgba(0,0,0,0.87)',
                                boxShadow: 3,
                                p: 2,
                                borderRadius: 2,
                                fontSize: '1rem',
                                maxWidth: 300,
                              }
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center">
                            <PhoneIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                            {selectedProspect.telefono || selectedProspect.apolloData?.organization?.primaryPhoneSanitized}
                            <InfoIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.5, width: 14, height: 14 }} />
                          </Box>
                        </Tooltip>
                      }
                    />
                  </ListItem>
                )}
                {selectedProspect?.apolloData?.organization?.primaryPhoneSanitized && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <BusinessIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {selectedProspect.apolloData.organization.name || 'Empresa actual'}
                          </Typography>
                          <Tooltip title="Llamar a la empresa" placement="top">
                            <a href={`tel:${selectedProspect.apolloData.organization.primaryPhoneSanitized}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                              <Typography variant="body1" sx={{ 
                                '&:hover': { textDecoration: 'underline', color: '#1976d2' },
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                color: 'rgba(0, 0, 0, 0.87)'
                              }}>
                                {selectedProspect.apolloData.organization.primaryPhoneSanitized}
                              </Typography>
                            </a>
                          </Tooltip>
                        </Box>
                      }
                    />
                  </ListItem>
                )}

                {/* Show divider if there's job information to display */}
                {(selectedProspect.jobTitle || selectedProspect.empresa) && <Divider sx={{ my: 1 }} />}

                {/* Show job title only if it's not already displayed in the header */}
                {((selectedProspect.jobTitle && !selectedProspect?.empleos?.find(job => job.esActual)?.titulo) || 
                  (selectedProspect?.empleos && !selectedProspect.empleos.find(job => job.esActual) && selectedProspect.jobTitle)) && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <WorkIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ 
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            color: 'rgba(0, 0, 0, 0.87)'
                          }}>
                            {selectedProspect.jobTitle || 'No especificado'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                )}
                {/* Show company only if it's not already displayed in the header */}
                {((selectedProspect.empresa && !selectedProspect?.empleos?.find(job => job.esActual)?.organizacionNombre) || 
                  (selectedProspect?.empleos && !selectedProspect.empleos.find(job => job.esActual) && selectedProspect.empresa)) && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <BusinessIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="body1" sx={{ 
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: 'rgba(0, 0, 0, 0.87)'
                        }}>
                          {selectedProspect.empresa || 'No especificado'}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}

                {(selectedProspect.linkedInUrl || selectedProspect?.apolloData?.city) && <Divider sx={{ my: 1 }} />}
                
                {selectedProspect?.linkedInUrl && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LinkedInIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Tooltip title="Ver perfil de LinkedIn" placement="top">
                          <a 
                            href={selectedProspect.linkedInUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <Typography variant="body1" sx={{ 
                              '&:hover': { textDecoration: 'underline', color: '#1976d2' },
                              fontSize: '0.95rem',
                              fontWeight: 500,
                              color: 'rgba(0, 0, 0, 0.87)'
                            }}>
                              Perfil de LinkedIn
                            </Typography>
                          </a>
                        </Tooltip>
                      }
                    />
                  </ListItem>
                )}
                {(selectedProspect?.apolloData?.city || selectedProspect?.apolloData?.state || selectedProspect?.apolloData?.country) && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LocationOnIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="body1" sx={{ 
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: 'rgba(0, 0, 0, 0.87)'
                        }}>
                          {[
                            selectedProspect.apolloData?.city,
                            selectedProspect.apolloData?.state,
                            selectedProspect.apolloData?.country
                          ].filter(Boolean).join(', ')}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </div>
          )}

          {(selectedProspect?.localidad || selectedProspect?.departamento || selectedProspect?.paisDeNacimiento || selectedProspect?.universidad) && (
            <div className="apollo-data-section">
              <h3>Información Personal</h3>
              <List>
                {(selectedProspect?.localidad || selectedProspect?.departamento || selectedProspect?.paisDeNacimiento) && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <LocationOnIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="body1" sx={{ 
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: 'rgba(0, 0, 0, 0.87)'
                        }}>
                          <strong>Ubicación:</strong> {[
                            selectedProspect.localidad,
                            selectedProspect.departamento,
                            selectedProspect.paisDeNacimiento
                          ].filter(Boolean).join(', ')}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {selectedProspect?.fechaDeNacimiento && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CalendarTodayIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="body1" sx={{ 
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: 'rgba(0, 0, 0, 0.87)'
                        }}>
                          <strong>Fecha de Nacimiento:</strong> {formatDate(selectedProspect.fechaDeNacimiento)}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {selectedProspect?.universidad && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <SchoolIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="body1" sx={{ 
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: 'rgba(0, 0, 0, 0.87)'
                        }}>
                          <strong>Universidad:</strong> {selectedProspect.universidad}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
                
                {selectedProspect?.mensajeBPS && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AssignmentIndIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="body1" sx={{ 
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: 'rgba(0, 0, 0, 0.87)'
                        }}>
                          <strong>Estado BPS:</strong> {selectedProspect.mensajeBPS}
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </div>
          )}

          {selectedProspect?.empleos && selectedProspect.empleos.length > 0 && (
            <div className="apollo-data-section">
              <span className="apollo-data-badge">Procesado por Apollo</span>
              <h3>Historial Laboral</h3>
              <Stepper orientation="vertical" className="employment-history">
                {selectedProspect.empleos
                  .sort((a, b) => {
                    // Sort by current status first, then by start date
                    if (a.esActual !== b.esActual) {
                      return a.esActual ? -1 : 1;
                    }
                    return new Date(b.fechaInicio) - new Date(a.fechaInicio);
                  })
                  .map((job, idx) => (
                    <Step key={idx} active completed>
                      <StepLabel
                        StepIconComponent={() => (
                          <Avatar
                            sx={{
                              bgcolor: job.esActual ? '#1976d2' : 'rgba(27, 40, 94, 0.7)',
                              width: 32,
                              height: 32,
                              fontSize: '0.9rem',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {job.organizacionNombre ? job.organizacionNombre.charAt(0).toUpperCase() : 'E'}
                          </Avatar>
                        )}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight="600" color="primary">
                            {job.titulo || 'No especificado'}
                          </Typography>
                          {job.esActual && (
                            <Chip 
                              label="Actual" 
                              size="small" 
                              className="current-job-chip"
                              sx={{ ml: 1, height: 20, fontWeight: 'bold' }}
                            />
                          )}
                        </Box>
                      </StepLabel>
                      <StepContent sx={{ backgroundColor: 'rgba(255,255,255,0.7)', p: 1.5, borderRadius: '0 0 4px 4px' }}>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                          <strong>{job.organizacionNombre || 'Empresa no especificada'}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                          {formatDate(job.fechaInicio)} - {job.esActual ? 'Presente' : formatDate(job.fechaFin)}
                        </Typography>
                        {job.descripcion && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            "{job.descripcion}"
                          </Typography>
                        )}
                      </StepContent>
                    </Step>
                  ))}
              </Stepper>
            </div>
          )}

          {selectedProspect?.educacion && selectedProspect.educacion.length > 0 && (
            <div className="apollo-data-section">
              <span className="apollo-data-badge">Procesado por Apollo</span>
              <h3>Educación</h3>
              <Stepper orientation="vertical" className="education-history">
                {selectedProspect.educacion
                  .sort((a, b) => {
                    // Sort by end date (most recent first)
                    return new Date(b.fechaFin || '9999-12-31') - new Date(a.fechaFin || '9999-12-31');
                  })
                  .map((edu, idx) => (
                    <Step key={idx} active completed>
                      <StepLabel
                        StepIconComponent={() => (
                          <Avatar
                            sx={{
                              bgcolor: 'rgba(63, 81, 181, 0.8)',
                              width: 32,
                              height: 32,
                              fontSize: '0.9rem',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            {edu.institucion ? edu.institucion.charAt(0).toUpperCase() : 'E'}
                          </Avatar>
                        )}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight="600" color="primary">
                            {edu.titulo || 'No especificado'}
                          </Typography>
                        </Box>
                      </StepLabel>
                      <StepContent sx={{ backgroundColor: 'rgba(255,255,255,0.7)', p: 1.5, borderRadius: '0 0 4px 4px' }}>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
                          <strong>{edu.institucion || 'Institución no especificada'}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.primary">
                          {formatDate(edu.fechaInicio)} - {formatDate(edu.fechaFin) || 'Presente'}
                        </Typography>
                        {edu.descripcion && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            "{edu.descripcion}"
                          </Typography>
                        )}
                      </StepContent>
                    </Step>
                  ))}
              </Stepper>
            </div>
          )}
        </Box>

        {/* Add other sections like skills, etc. here */}
      </div>
    );
  };

  return (
    <Container maxWidth={false}>
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <div className="dashboard-container">
        {/* Sección de Filtros */}
        <div className="filters-section">
          <Typography className="filters-title">
            Filtros
          </Typography>
          
          <div className="scrollable-content">
            {/* Chips de filtros activos */}
            <Box className="filter-chips">
              {Object.keys(filters).map((key) => {
                const value = filters[key];
                const filterDefinition = availableFilters.find((f) => f.name === key);
                
                // Skip null, undefined, or empty string values
                if (value === null || value === undefined || value === '') return null;
                // Skip empty arrays
                if (Array.isArray(value) && value.every(v => !v)) return null;
                
                // Special handling for boolean values - always show them regardless of true/false
                const isBooleanFilter = filterDefinition?.type === 'boolean';
                const displayValue = isBooleanFilter 
                  ? (value ? 'Sí' : 'No')
                  : (Array.isArray(value)
                      ? value.filter(Boolean).join(' - ')
                      : typeof value === 'object' && value !== null
                        ? [
                            value.start ? new Date(value.start).toLocaleDateString('es-UY') : '',
                            value.end ? new Date(value.end).toLocaleDateString('es-UY') : ''
                          ].filter(Boolean).join(' - ')
                        : value.toString());

                return (
                  <Chip
                    key={key}
                    label={`${filterDefinition?.label || key}: ${displayValue}`}
                    onDelete={() => handleFilterDelete(key)}
                    className="filter-chip"
                  />
                );
              })}
            </Box>

            {/* Secciones de filtros */}
            {availableFilters && availableFilters.length > 0 ? (
              availableFilters.map((filter) => (
                <div 
                  key={filter.name} 
                  className={`filter-section ${filters[filter.name] ? 'has-selected' : ''}`}
                >
                  <Typography className="filter-label">
                    {filter.label}
                  </Typography>
                  {renderFilterControl(filter)}
                </div>
              ))
            ) : (
              <Box p={2} textAlign="center">
                <Typography color="textSecondary">
                  {loadingFilters ? 'Cargando filtros...' : 'No hay filtros disponibles'}
                </Typography>
                {loadingFilters && (
                  <CircularProgress size={24} sx={{ mt: 2 }} />
                )}
              </Box>
            )}
          </div>
        </div>

        {/* Sección de Contenido */}
        <div className="content-section">
          <div className="content-header">
            <div>
              <Typography className="dashboard-title">
                Prospectos
              </Typography>
              <Typography className="results-count">
                {totalProspects} resultados
              </Typography>
              <Box display="flex" alignItems="center" mt={1}>
                <Box 
                  width={16} 
                  height={16} 
                  bgcolor="rgba(144, 202, 249, 0.15)" 
                  border="1px solid rgba(144, 202, 249, 0.5)"
                  borderRadius={1}
                  mr={1}
                />
                <Typography variant="caption" color="textSecondary">
                  Procesado por Apollo
                </Typography>
              </Box>
            </div>
            <div className="export-buttons">
              <Button
                variant="contained"
                onClick={exportToExcel}
                disabled={!rows.length}
                startIcon={<GetAppIcon />}
                size="small"
              >
                Exportar a Excel
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={exportToPDF}
                disabled={!rows.length}
                startIcon={<PictureAsPdfIcon />}
                size="small"
              >
                Exportar a PDF
              </Button>
            </div>
          </div>

          <div className="scrollable-content">
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow className="table-header-row">
                    <TableCell className="dashboard-table-header">Nombre</TableCell>
                    <TableCell className="dashboard-table-header">Apellido</TableCell>
                    <TableCell className="dashboard-table-header">Cedula</TableCell>
                    <TableCell className="dashboard-table-header">Fecha de Nacimiento</TableCell>
                    <TableCell className="dashboard-table-header">Email</TableCell>
                    <TableCell className="dashboard-table-header">Teléfono</TableCell>
                    <TableCell className="dashboard-table-header">Teléfono Empresa</TableCell>
                    <TableCell className="dashboard-table-header">Universidad</TableCell>
                    <TableCell className="dashboard-table-header">Cargo Actual</TableCell>
                    <TableCell className="dashboard-table-header">Empresa</TableCell>
                    <TableCell className="dashboard-table-header">LinkedIn</TableCell>
                    <TableCell className="dashboard-table-header">Ciudad</TableCell>
                    <TableCell className="dashboard-table-header">Estado/Departamento</TableCell>
                    <TableCell className="dashboard-table-header">País</TableCell>
                    <TableCell className="dashboard-table-header">Estado de afiliacion</TableCell>
                    <TableCell className="dashboard-table-header">Estado Apollo</TableCell>
                    <TableCell className="dashboard-table-header">Batch</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow className="table-row">
                      <TableCell colSpan={16} align="center" sx={{ height: '48px' }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : rows.length > 0 ? (
                    rows.map((row) => (
                      <TableRow 
                        key={row.id} 
                        hover 
                        className={`table-row ${isProcessedByApollo(row) ? 'apollo-processed-row' : ''}`}
                        sx={{ 
                          opacity: loading ? 0.5 : 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => handleRowClick(row)}
                      >
                        <TableCell className="dashboard-table-cell">{row.nombre}</TableCell>
                        <TableCell className="dashboard-table-cell">{row.apellido}</TableCell>
                        <TableCell className="dashboard-table-cell">{row.cedula}</TableCell>
                        <TableCell className="dashboard-table-cell">{formatDate(row.fechaDeNacimiento)}</TableCell>
                        <TableCell className="dashboard-table-cell">
                          <Box display="flex" alignItems="center" height="100%">
                            {row.email && (
                              <>
                                <EmailIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                                {row.email}
                              </>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell className="dashboard-table-cell">
                          <Box display="flex" alignItems="center" height="100%">
                            {row.telefono && (
                              <Box display="flex" alignItems="center">
                                <PhoneIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                                {row.telefono}
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell className="dashboard-table-cell">
                          <Box display="flex" alignItems="center" height="100%">
                            {(row.apolloData?.organization?.primaryPhoneSanitized) && (
                              <Tooltip 
                                title={
                                  <Box>
                                    {row.apolloData?.organization?.primaryPhoneSanitized && (
                                      <Box>
                                        <Typography variant="subtitle2" color="primary">
                                          Teléfono de {row.apolloData.organization.name || 'la empresa'}
                                        </Typography>
                                        <Typography>{row.apolloData.organization.primaryPhoneSanitized}</Typography>
                                      </Box>
                                    )}
                                  </Box>
                                }
                                placement="left"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: 'white',
                                      color: 'rgba(0,0,0,0.87)',
                                      boxShadow: 3,
                                      p: 2,
                                      borderRadius: 2,
                                      fontSize: '1rem',
                                      maxWidth: 300,
                                    }
                                  }
                                }}
                              >
                                <Box display="flex" alignItems="center">
                                  <PhoneIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                                  {row.apolloData?.organization?.primaryPhoneSanitized}
                                  <InfoIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.5, width: 14, height: 14 }} />
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell className="dashboard-table-cell">{row.universidad}</TableCell>
                        <TableCell className="dashboard-table-cell">
                          <Tooltip 
                            title={row.apolloData?.headline || row.jobTitle || ''} 
                            placement="top"
                          >
                            <Box display="flex" alignItems="center" height="100%">
                              <WorkIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                              {row.jobTitle || (row.apolloData?.employmentHistory && row.apolloData.employmentHistory.length > 0 
                                ? row.apolloData.employmentHistory.find(job => job.current)?.title || '' 
                                : '')}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="dashboard-table-cell">
                          {row.apolloData?.employmentHistory?.length > 0 ? (
                            <Tooltip 
                              title={
                                <Typography style={{ whiteSpace: 'pre-line' }} className="employment-history-tooltip">
                                  <strong>Historial laboral:</strong>
                                  <br />
                                  {formatEmploymentHistory(row.apolloData.employmentHistory)}
                                </Typography>
                              } 
                              placement="left"
                              classes={{ tooltip: 'employment-history-tooltip' }}
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: 'white',
                                    color: 'rgba(0, 0, 0, 0.87)',
                                    maxWidth: 500,
                                    fontSize: '0.9rem',
                                    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.15)',
                                    p: 2,
                                    borderRadius: 2
                                  }
                                }
                              }}
                            >
                              <Box display="flex" alignItems="center">
                                <BusinessIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                                {row.empresa || (row.apolloData?.employmentHistory && row.apolloData.employmentHistory.length > 0 
                                  ? row.apolloData.employmentHistory.find(job => job.current)?.organizationName || row.apolloData.employmentHistory[0].organizationName 
                                  : '')}
                                <InfoIcon fontSize="small" sx={{ ml: 0.5, opacity: 0.5, width: 14, height: 14 }} />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box display="flex" alignItems="center">
                              <BusinessIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                              {row.empresa || ''}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell className="dashboard-table-cell">
                          {row.linkedInUrl && (
                            <Button 
                              href={row.linkedInUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              size="small"
                              variant="text"
                              className="linkedin-button"
                              startIcon={<LinkedInIcon />}
                              sx={{ minWidth: 'unset', padding: '2px 8px' }}
                            >
                              Ver
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="dashboard-table-cell">
                          {row.apolloData?.city && (
                            <Box display="flex" alignItems="center">
                              <LocationOnIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                              {row.apolloData.city}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell className="dashboard-table-cell">
                          {(row.apolloData?.state || row.departamento?.value || row.departamento) && (
                            <Box display="flex" alignItems="center">
                              <LocationOnIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                              {row.apolloData?.state || row.departamento?.value || row.departamento || ''}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell className="dashboard-table-cell">
                          {(row.apolloData?.country || row.paisDeNacimiento) && (
                            <Box display="flex" alignItems="center">
                              <LocationOnIcon fontSize="small" sx={{ mr: 0.5, opacity: 0.6, width: 16, height: 16 }} />
                              {row.apolloData?.country || row.paisDeNacimiento || ''}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell className="dashboard-table-cell">{row.mensajeBPS}</TableCell>
                        <TableCell className="dashboard-table-cell">
                          {isProcessedByApollo(row) ? (
                            <Box display="flex" alignItems="center">
                              <CheckCircleIcon 
                                fontSize="small" 
                                color="primary" 
                                sx={{ mr: 1, opacity: 0.7 }} 
                              />
                              {formatDate(row.apolloProcessedDate)}
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              Pendiente
                            </Box>
                          )}
                        </TableCell>
                        <TableCell className="dashboard-table-cell">{row.batchName}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow className="table-row">
                      <TableCell colSpan={16} align="center" sx={{ height: '48px' }}>
                        <Typography variant="body1" color="textSecondary">
                          No se encontraron registros
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, 200]}
            component="div"
            count={totalProspects}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage="Filas por página:"
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </div>

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '60%', md: '40%' },
            maxWidth: '600px',
            overflow: 'auto',
            padding: 0,
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: '#f9f9f9',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.05)'
          }
        }}
      >
        {renderDrawer()}
      </Drawer>
    </Container>
  );
};

export default Dashboard;
